import {
  empty_address,
  empty_payment,
  expandCheckoutInfo,
  nexus,
  nexusEqual,
  validateInfo
} from '@/lib/CheckoutLogic';
import {calculateSalesTax, getOrderInfo, placeOrder, shippingMethods} from '@/services/checkout';
import {createToken, loadStripe} from '@/services/stripe';
import {postVerifyAddressData} from '@/services/addresses';
import {getCookie, setCookie} from 'tiny-cookie';
import {orderPlaced} from '@/lib/track/events';

export default {
  state: {
    nexus: null,
    taxInfo: {
      amount: null,  // Amount not known
      busy: false,   // API call not in progress
      error: null,   // API call had error
      errCount: 0,
    },
    shipInfo: {
      amount: null,  // Amount not known
      code: null,    // Shipping code for selected method
      methods: [],   // list of available shipping methods
      busy: false,   // API call not in progress
    },
    checkout: {
      billing: empty_address(),
      shipping: empty_address(),
      shippingAddressVerification: null,
      billing_different_address: false,
      skip_review: false,
      reviewer: null,
      managerCheckbox: null,
    },
    paymentInfo: {
      // Payment info, this should not be sent anywhere except to stripe!
      data: empty_payment(),

      // Data from stripe request
      stripeResult: null,

      // Whether we're currently trying to get the stripe info
      busy: false,

      // Stripe error object
      error: null,
    },
    orderInfo: {
      placed: false,
      busy: false,
      details: null,
      error: null
    }
  },
  mutations: {
    updateNexus(state, value) {
      state.nexus = value;
      // Reset error count for this nexus
      state.taxInfo.errCount = 0;
    },
    startTaxCalc(state) {
      state.taxInfo.amount = null;
      state.taxInfo.busy = true;
      state.taxInfo.error = null;
    },
    taxCalcFailed(state, error) {
      state.taxInfo.busy = false;
      state.taxInfo.error = error;
      state.taxInfo.errCount++;
    },
    updateTaxAmount(state, value) {
      state.taxInfo.amount = value;
      state.taxInfo.busy = false;
      state.taxInfo.errCount = 0;;
      state.taxInfo.error = null;
    },
    startShipCalc(state) {
      state.shipInfo.amount = null;
      state.shipInfo.busy = true;
    },
    updateShipMethods(state, methods) {
      state.shipInfo.methods = methods;
      if (!methods || methods.length === 0) {
        // No methods -- clear out amount
        state.shipInfo.code = null;
        state.shipInfo.amount = null;
      } else {
        let method = findCode(methods, state.shipInfo.code);
        if (!method) {
          // Previous code not valid -- choose first code as option
          method = methods[0];
          state.shipInfo.code = method.code;
        }
        state.shipInfo.amount = method.price;
      }
      state.shipInfo.busy = false;
    },
    updateShipCode(state, code) {
      let method = findCode(methods, state.shipInfo.code);
      if (!method) throw "Ship code not found!";
      state.shipInfo.code = method.code;
      state.shipInfo.amount = method.price;
    },
    updateCheckout(state, checkoutData) {
      state.checkout = checkoutData;
    },
    updatePayment(state, paymentData) {
      state.paymentInfo.data = paymentData;
    },
    startPayment(state) {
      state.paymentInfo.busy = true;
      // Clear out previous results
      state.paymentInfo.error = null;
      state.paymentInfo.stripeResult = null;
    },
    paymentSucceeded(state, stripeResult) {
      state.paymentInfo.busy = false;
      state.paymentInfo.stripeResult = stripeResult;
    },
    paymentFailed(state, error) {
      state.paymentInfo.busy = false;
      state.paymentInfo.error = error;
    },
    paymentSkipped(state) {
      // This information is not currently used but will
      // be recorded in Vue log for debuggability
      state.paymentInfo.skipped = true;
    },
    startPlaceOrder(state) {
      console.assert(!state.orderInfo.placed);
      state.orderInfo.busy = true;
      state.orderInfo.error = null;
    },
    placeOrderFailed(state, error) {
      state.orderInfo.error = error;
      state.orderInfo.busy = false;
    },
    placeOrderSucceeded(state, details) {
      state.orderInfo.details = details;
      state.orderInfo.placed = true;
      state.orderInfo.busy = false;
    },
    loadOrderStarted(state) {
      state.orderInfo.busy = true;
      state.orderInfo.error = null;
    },
    loadOrderSucceeded(state, details) {
      state.orderInfo.details = details;
      state.orderInfo.placed = true;
      state.orderInfo.busy = false;
    },
    loadOrderFailed(state, error) {
      state.orderInfo.error = error;
      state.orderInfo.busy = false;
    },
    setShippingAddressVerification(state, data) {
      state.checkout.shippingAddressVerification = data;
    },
    removeShippingAddressVerification(state) {
      state.checkout.shippingAddressVerification = null;
    }
  },
  actions: {
    /**
     * Updates current checkout data.
     *
     * @param {*} context
     * @param {*} checkoutData
     */
    async updateCheckoutInfo(context, checkoutData) {
      context.commit('updateCheckout', checkoutData);
      return context.dispatch('updateNexus', nexus(checkoutData));
      // TODO also update the shipping location, etc...
    },
    /**
     * Sets default shipping and billing addresses.
     *
     * @param dispatch
     * @param getters
     * @returns {Promise<void>}
     */
    async setDefaultCheckoutInfo({ dispatch, getters }) {
      await dispatch('loadUserAddresses');

      const addresses = getters.userAddresses;
      const shipping = addresses.find(addr => addr.is_default_for_shipping) || addresses[0];
      const billing = addresses.find(addr => addr.is_default_for_billing) || shipping;

      // Since we use address from user's address book, it may already be verified
      let shippingAddressVerification = null;
      if (shipping.address_verification) {
        shippingAddressVerification = shipping.address_verification.verification_data;
      }
      let checkout = Object.assign({}, getters.checkoutInfo, {billing, shipping, shippingAddressVerification});
      dispatch('updateCheckoutInfo', checkout);
    },
    async updateNexus(context, nexus) {
      if (context.getters.taxAmount === null || !nexusEqual(context.state.nexus, nexus)) {
        context.commit('updateNexus', nexus);
        if (nexus) {
          context.dispatch('calcTax');
          context.dispatch('calcShipping');
        }
      }
    },
    async calcTax({state, commit, getters}) {
      if (!getters.basketLines.length) {
        console.log('ignoring calcTax until basket is loaded');
        return;
      }
      if (!state.nexus) {
        console.log('ignoring calcTax until nexus is defined');
        return;
      }
      if (state.taxInfo.errCount > 2) {
        console.log('ignoring taxCalc due to too many errors recently');
        return;
      }
      commit('startTaxCalc');
      try {
        let taxAmount = await calculateSalesTax(state.nexus, getters.basketLines);
        commit('updateTaxAmount', taxAmount);
      } catch (e) {
        //console.error(e);
        // Unwrap the error body. Typically this won't have much useful info,
        // but its the best we have.
        if (e.response && e.response.data) e = e.response.data;
        commit('taxCalcFailed', e);
      }
    },
    async calcShipping({state, commit}) {
      commit('startShipCalc');
      let methods = await shippingMethods();
      commit('updateShipMethods', methods);
    },

    async updatePaymentInfo({commit}, paymentData) {
      commit('updatePayment', paymentData);
    },

    async loadStripe() {
      loadStripe();
    },

    async createStripeToken({state, dispatch, commit, getters}) {
      // Create stripe token if needed. Return true if
      // order can proceed.
      if (!getters.needsPayment) {
        commit('paymentSkipped');
        return true;
      }
      if (state.paymentInfo.data.saved_card) {
        // Use a null stripe token
        commit('paymentSucceeded', null);
        return true;
      }
      let checkout = expandCheckoutInfo(state.checkout);
      commit('startPayment');
      var stripeResult;
      try {
        stripeResult = await createToken(state.paymentInfo.data, checkout.billing);
      } catch(e) {
        console.error(e);
        commit('paymentFailed', e);
        return false;
      }
      commit('paymentSucceeded', stripeResult);
      return true;
    },
    async placeOrder({state, dispatch}) {
      if (await dispatch('createStripeToken')) {
        await dispatch('submitOrder');
        dispatch('reloadBasket'); // Basket will be empty, sync state with api

        // Set a session cookie that allows the invoice page
        // to gracefully show the most recently placed order
        if (state.orderInfo.details && state.orderInfo.details.url) {
          setCookie('lastOrder', state.orderInfo.details.url, {});
        } else {
          console.error("lastOrder not set");
        }
      }
    },
    async submitOrder({state, dispatch, commit, getters}) {
      commit('startPlaceOrder');
      let {shipping, billing, ...rest} = expandCheckoutInfo(state.checkout);
      let shipping_charge = {excl_tax: state.shipInfo.amount, tax: 0, incl_tax: state.shipInfo.amount};
      try {
        // Round the total price here, because Django-oscar-api serializers will fail if the number
        // has more than two decimals. It does accept strings however.
        let total = getters.totalPrice.toFixed(2);
        // If the payment info is skipped, tell the backend via
        // a special value for StripeToken.
        let {skipped, data, stripeResult} = state.paymentInfo;
        let stripeToken, paymentMethod, title, is_private;
        if (!skipped) {
          if (data.saved_card) {
            paymentMethod = 'SavedCard'
            stripeToken = data.saved_card;
          } else {
            stripeToken = stripeResult && stripeResult.id;
            if (data.save_card) {
              paymentMethod = 'CardSave';
              title = data.title;
              is_private = data.is_private;
            } else {
              paymentMethod = 'Card';
            }
          }
        } else {
          if (getters.skipPaymentReason === 'centralizedPayment') {
            stripeToken = 'Defer';
            paymentMethod = 'DeferToApproval';
          } else if (getters.forceInvoice) {
            stripeToken = 'Defer';
            paymentMethod = 'Invoice';
          } else {
            stripeToken =  'FreeVoucher';
            paymentMethod = 'Voucher';
          }
        }
        let orderInfo = await placeOrder({
          shipping_charge,
          total,
          billing_address: billing,
          shipping_address: shipping,
          basket: getters.basketUrl,
          guest_email: shipping.email_address,
          ...rest
        }, {paymentMethod, stripeToken, title, is_private}, getters.basketLines);
        let numItems = orderInfo.lines.length;
        orderPlaced({paymentMethod, numItems, total});
        commit('placeOrderSucceeded', orderInfo);
        if (data.save_card) {
          dispatch('loadSavedCards');
        }
        dispatch('updateRecentOrders')
      } catch(e) {
        console.error(e);
        if (e.response && e.response.data) e = e.response.data;
        if (e.detail) e = e.detail;
        if (typeof e === 'string' && e.trim()[0] === '<' && e.length > 40) {
          // We have a big HTML server error page. There's no useful information,
          // make a nicer message.
          e = 'Server error occurred';
        }
        commit('placeOrderFailed', e);
      }
    },
    async loadLastOrder({state, commit}) {
      // Nothing to do if order is in state already
      if (state.orderInfo.details) return;

      // Restore from cookie
      let orderUrl = getCookie('lastOrder');
      if (!orderUrl) {
        commit('loadOrderFailed', "Can't find last order");
      } else {
        commit('loadOrderStarted');
        try {
          let orderInfo = await getOrderInfo(orderUrl);
          commit('loadOrderSucceeded', orderInfo);
        } catch(e) {
          commit('loadOrderFailed', e+'');
        }
      }
    },
    async setLastOrder({dispatch}, id) {
      setCookie('lastOrder', id, {});
      return dispatch('loadLastOrder');
    },
    async verifyShippingAddressData({commit, dispatch}, addressData) {
      const verificationData = await postVerifyAddressData({...addressData, line4: addressData.city});
      commit('setShippingAddressVerification', verificationData);
    }
  },
  getters: {
    taxAmount: state => state.taxInfo.amount,
    shipAmount: state => state.shipInfo.amount,
    isShippingCountryUSA: state => state.checkout.shipping.country === "US",
    nexusInfoComplete(state, getters) {
      return state.nexus && (!getters.needsPayment || state.taxInfo.amount !== null) && !state.taxInfo.busy && state.shipInfo.code && !state.shipInfo.busy;
    },
    paymentInfoComplete(state, getters) {
      return getters.nexusInfoComplete && validateInfo({
        checkout: state.checkout,
        payment: state.paymentInfo.data,
        needsPayment: getters.needsPayment
      });
    },
    checkoutIssues(state, getters) {
      let issues = [];
      if (state.taxInfo.busy) {
        issues.push('Calculating tax');
      } else if (state.nexus && getters.needsPayment && state.taxInfo.amount == null) {
        issues.push('Unable to calculate tax');
      }
      if (state.shipInfo.busy) {
        issues.push('Calculating shipping');
      } else if (state.nexus && !state.shipInfo.code) {
        issues.push('Unable to calculate shipping');
      }
      validateInfo({
        checkout: state.checkout,
        payment: state.paymentInfo.data,
        needsPayment: getters.needsPayment
      }, issues);
      if (state.paymentInfo.error) {
        issues.push(stringify(state.paymentInfo.error));
      }
      if (state.orderInfo.error) {
        issues.push(stringify(state.orderInfo.error));
      }
      if (!getters.isShippingAddressValid) {
        issues.push('Shipping address is invalid');
      }
      if (getters.approvalSettings.requireNotifyEmail) {
        if (!state.checkout.reviewer) {
          issues.push('You must enter a manager email');
        }
        if (!state.checkout.managerCheckbox) {
          issues.push('You must check the box');
        }
      }
      return issues;
    },
    checkoutInfo: state => state.checkout,
    skipReview: state => (state.checkout||{}).skip_review,
    checkoutBusy: state => state.paymentInfo.busy || state.orderInfo.busy,
    orderInfo: state => state.orderInfo,
    orderLines: state => state.orderInfo && state.orderInfo.details && state.orderInfo.details.lines,
    needsPayment: (state, getters) => !getters.skipPaymentReason,
    skipPaymentReason: (state, getters) => {
      if (getters.totalPrice === 0 && getters.subtotalPrice === 0) {
        return 'free';
      } else if (getters.approvalSettings.approvalMode === 'payment' && !getters.skipReview) {
        return 'centralizedPayment';
      } else if (getters.forceInvoice) {
        return 'invoice';
      }
      return null;
    },
    orderVoucher: state => {
      let o = state.orderInfo;
      o = o && o.details;
      o = o && o.voucher_discounts;
      return o && o[0];
    },
    canSaveCardOnFile: (state, getters) => getters.hasTeam && getters.isAdmin,
    hasCardOnFile: (state, getters) => getters.hasTeam && getters.team.stripe_customer,
    shippingAddressVerification: (state) => state.checkout.shippingAddressVerification,
    isShippingAddressValid: (state, getters) => {
      if (getters.flags.verifyCheckoutShippingAddressData && state.checkout.shippingAddressVerification) {
        const data = state.checkout.shippingAddressVerification;
        if (!data.verifications.delivery.success) {
          return false;
        }
      }
      // Assume that shipping address is always valid w/o verifications
      return true;
    }
  }
}

function findCode(methods, codeToFind) {
  return methods.find(({code}) => code === codeToFind);
}

function stringify(obj) {
  // Turn object into a string without losing information
  return typeof obj === 'object' ? JSON.stringify(obj) : obj.toString();
}



// WEBPACK FOOTER //
// ./src/store/checkout.js